import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { dayOfWeekUTC, todayKST } from '../../../utils/date';
import { badgeStyle } from './styles';
import {
  DateEventDetails,
  EventSummaryButton,
  UpcomingEventList,
  primaryEventTone,
} from './ContractRenewalCalendarEvents';
import {
  buildCells,
  collectEvents,
  monthKey,
  monthLabel,
  monthParts,
  shiftMonthKey,
} from './ContractRenewalCalendar.helpers';
import {
  calendarHeaderStyle,
  calendarPanelStyle,
  calendarSubStyle,
  calendarTitleStyle,
  calendarToolbarStyle,
  dateGridStyle,
  dateRowStyle,
  dayCellStyle,
  dayNumberStyle,
  eventDayCellStyle,
  eventListStyle,
  holidayCellStyle,
  holidayDayNumberStyle,
  holidayNameStyle,
  legendDotStyle,
  legendItemStyle,
  legendStyle,
  mobileMonthLabelStyle,
  mobileMonthNavStyle,
  mobileMoveButtonStyle,
  monthBoardStyle,
  monthBoardViewportStyle,
  monthCardStyle,
  monthHeadStyle,
  monthListStyle,
  moveButtonStyle,
  saturdayCellStyle,
  saturdayDayNumberStyle,
  saturdayWeekLabelStyle,
  sundayCellStyle,
  sundayDayNumberStyle,
  sundayWeekLabelStyle,
  todayCellStyle,
  weekGridStyle,
  weekLabelStyle,
} from './ContractRenewalCalendar.styles';

const WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function MonthCalendar({ month, eventsByDate, holidaysByDate, selectedDate, today, onSelectDate }) {
  const cells = buildCells(month);
  return (
    <section style={monthCardStyle}>
      <div style={monthHeadStyle}>
        <strong>{monthLabel(month)}</strong>
      </div>
      <div style={weekGridStyle}>
        {WEEK_LABELS.map((label, index) => (
          <div
            key={label}
            style={{
              ...weekLabelStyle,
              ...(index === 0 ? sundayWeekLabelStyle : null),
              ...(index === 6 ? saturdayWeekLabelStyle : null),
            }}
          >
            {label}
          </div>
        ))}
      </div>
      <div style={dateGridStyle}>
        {cells.map((date, index) => {
          const events = date ? eventsByDate[date] || [] : [];
          const holiday = date ? holidaysByDate[date] : null;
          const dow = date ? dayOfWeekUTC(date) : null;
          const isHoliday = Boolean(holiday);
          const isSunday = dow === 0;
          const isSaturday = dow === 6;
          const primaryTone = primaryEventTone(events);
          const numberStyle = isHoliday
            ? holidayDayNumberStyle
            : isSunday
              ? sundayDayNumberStyle
              : isSaturday
                ? saturdayDayNumberStyle
                : dayNumberStyle;
          return (
            <div
              key={date || `empty-${index}`}
              style={{
                ...dayCellStyle,
                ...(isSunday ? sundayCellStyle : null),
                ...(isSaturday ? saturdayCellStyle : null),
                ...(isHoliday ? holidayCellStyle : null),
                ...(primaryTone ? eventDayCellStyle(primaryTone) : null),
                ...(date === today ? todayCellStyle : null),
                ...(date === selectedDate ? todayCellStyle : null),
                opacity: date ? 1 : 0.35,
              }}
            >
              {date && (
                <div style={dateRowStyle}>
                  <span style={numberStyle}>
                    {Number(date.slice(8, 10))}
                  </span>
                  {isHoliday && (
                    <span style={holidayNameStyle} title={holiday.name || '공휴일'}>
                      {holiday.name || '공휴일'}
                    </span>
                  )}
                </div>
              )}
              <div style={eventListStyle}>
                {events.length > 0 && (
                  <EventSummaryButton events={events} onClick={() => onSelectDate(date)} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function ContractRenewalCalendar({
  rows,
  onSelectRow,
  isMobile = false,
  apiBase = '/api/admin/employee-contract-renewals',
  companyCode = 'PINE',
}) {
  const today = todayKST();
  const [baseMonth, setBaseMonth] = useState(() => monthKey(today));
  const [selectedDate, setSelectedDate] = useState(null);
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [slideDirection, setSlideDirection] = useState('');
  const slideTimerRef = useRef(null);
  const sliding = Boolean(slideDirection);

  useEffect(() => {
    return () => {
      if (slideTimerRef.current) window.clearTimeout(slideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const { year, month } = monthParts(baseMonth);
    const params = new URLSearchParams({
      year: String(year),
      month: String(month),
      months: '3',
      company_code: String(companyCode || 'PINE').toUpperCase(),
    });

    apiClient.get(`${apiBase}/holidays?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        if (!res.ok || !res.data?.success) {
          setHolidaysByDate({});
          return;
        }
        const next = {};
        (res.data.data || []).forEach((item) => {
          (item.holidays || []).forEach((holiday) => {
            if (holiday?.ymd) next[holiday.ymd] = holiday;
          });
        });
        setHolidaysByDate(next);
      })
      .catch(() => {
        if (!cancelled) setHolidaysByDate({});
      });

    return () => {
      cancelled = true;
    };
  }, [apiBase, baseMonth, companyCode]);

  function moveMonth(delta) {
    if (slideTimerRef.current) window.clearTimeout(slideTimerRef.current);
    setSlideDirection(delta > 0 ? 'next' : 'prev');
    setSelectedDate(null);
    setBaseMonth((prev) => shiftMonthKey(prev, delta));
    slideTimerRef.current = window.setTimeout(() => setSlideDirection(''), 260);
  }

  const rangeMonths = useMemo(() => [0, 1, 2].map((offset) => shiftMonthKey(baseMonth, offset)), [baseMonth]);
  const months = useMemo(() => (isMobile ? [baseMonth] : rangeMonths), [baseMonth, isMobile, rangeMonths]);
  const eventsByDate = useMemo(() => collectEvents(rows, months, today), [months, rows, today]);
  const rangeEventsByDate = useMemo(() => collectEvents(rows, rangeMonths, today), [rangeMonths, rows, today]);
  const detailEventsByDate = isMobile ? rangeEventsByDate : eventsByDate;
  const selectedEvents = selectedDate ? detailEventsByDate[selectedDate] || [] : [];
  const total = Object.values(eventsByDate).reduce((sum, items) => sum + items.length, 0);
  const rangeTotal = Object.values(rangeEventsByDate).reduce((sum, items) => sum + items.length, 0);

  return (
    <aside style={calendarPanelStyle}>
      <div style={calendarHeaderStyle}>
        <div>
          <h3 style={calendarTitleStyle}>계약 일정</h3>
          <div style={calendarSubStyle}>
            {isMobile ? monthLabel(baseMonth) : `${monthLabel(months[0])} ~ ${monthLabel(months[2])}`}
          </div>
        </div>
        <div style={calendarToolbarStyle}>
          <div style={legendStyle}>
            <span style={legendItemStyle}><i style={{ ...legendDotStyle, background: '#dc2626' }} />수습</span>
            <span style={legendItemStyle}><i style={{ ...legendDotStyle, background: '#15803d' }} />계약</span>
            <span style={legendItemStyle}><i style={{ ...legendDotStyle, background: '#ef4444' }} />공휴일</span>
          </div>
          <span style={badgeStyle((isMobile ? rangeTotal : total) ? 'amber' : 'green')}>{isMobile ? rangeTotal : total}건</span>
        </div>
      </div>
      {isMobile && (
        <div style={mobileMonthNavStyle}>
          <button type="button" style={mobileMoveButtonStyle('left')} onClick={() => moveMonth(-1)} title="이전 달">
            <ChevronLeft size={24} />
          </button>
          <div style={mobileMonthLabelStyle}>{monthLabel(baseMonth)}</div>
          <button type="button" style={mobileMoveButtonStyle('right')} onClick={() => moveMonth(1)} title="다음 달">
            <ChevronRight size={24} />
          </button>
        </div>
      )}
      <div style={isMobile ? undefined : monthBoardStyle}>
        {!isMobile && (
          <button type="button" style={moveButtonStyle('left')} onClick={() => moveMonth(-1)} title="이전 달">
            <ChevronLeft size={34} />
          </button>
        )}
        <div style={monthBoardViewportStyle}>
          <div style={monthListStyle(sliding, slideDirection, isMobile)}>
            {months.map((month) => (
              <MonthCalendar
                key={month}
                month={month}
                eventsByDate={eventsByDate}
                holidaysByDate={holidaysByDate}
                selectedDate={selectedDate}
                today={today}
                onSelectDate={setSelectedDate}
              />
            ))}
          </div>
        </div>
        {!isMobile && (
          <button type="button" style={moveButtonStyle('right')} onClick={() => moveMonth(1)} title="다음 달">
            <ChevronRight size={34} />
          </button>
        )}
      </div>
      {selectedDate && (
        <DateEventDetails selectedDate={selectedDate} events={selectedEvents} onSelectRow={onSelectRow} />
      )}
      {isMobile && <UpcomingEventList eventsByDate={rangeEventsByDate} onSelectDate={setSelectedDate} />}
    </aside>
  );
}
